import AppShell from "@/components/AppShell";
import NewTaskCard from "@/components/NewTaskCard";
import DeleteConfirm from "@/components/DeleteConfirm";
import DetailsPanel from "@/components/DetailsPanel";
import { buildListOptions } from "@/lib/listOptions";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";
import { LEAVING_ROW_CLASSES, type TaskData } from "@/components/TaskRow";
import { createTask, deleteTask as mutateDelete, startTemplate } from "@/mutations/tasks";
import { useLocalView } from "@/hooks/useLocalView";
import { flushOutbox } from "@/lib/syncEngine";
import { isOfflineStandIn, OFFLINE_EMPTY_TEXT } from "@/lib/swClient";
import { templates as localTemplates } from "@/lib/views";
import { useLeavingRows } from "@/hooks/useLeavingRows";
import { pluralRu } from "@/lib/plural";
import type { SharedProps } from "@/types";
import { router, usePage } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { NEEDS_NETWORK, useOnline } from "@/hooks/useOnline";

interface Props {
  templates: TaskData[];
  fetchedAt?: string;
}

const STACK = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const PLAY = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ITEM_FORMS = ["пункт", "пункта", "пунктов"] as const;

export default function TemplatesShow(props: Props) {
  const online = useOnline();
  const local = useLocalView("templates", localTemplates) as TaskData[] | null;
  const templates = local ?? props.templates;
  const sidebar = usePage<SharedProps>().props.sidebar;
  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const [taskPendingDelete, setTaskPendingDelete] = useState<TaskData | null>(null);
  const [newTitle, setNewTitle] = useState("");

  const inboxId = sidebar?.inbox?.id ?? null;

  function createTemplate(title: string) {
    if (!inboxId) return;
    const giveTitleBack = () => setNewTitle((typed) => typed || title);
    if (createTask({ title, listId: inboxId, reusable: true }, { onRejected: giveTitleBack })) setNewTitle("");
  }

  function submitNewTemplate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newTitle.trim();
    if (!trimmed || !inboxId) return;
    createTemplate(trimmed);
  }

  const listOptions = buildListOptions(sidebar);

  const visible = useLeavingRows(templates);

  useEffect(() => {
    if (!selectedTask) return;
    const fresh = visible.find((t) => t.id === selectedTask.id);
    if (!fresh) setSelectedTask(null);
    else if (fresh !== selectedTask) setSelectedTask(fresh);
  }, [visible, selectedTask]);

  function confirmDeleteTask() {
    if (!taskPendingDelete) return;
    mutateDelete(taskPendingDelete.id);
    setTaskPendingDelete(null);
  }

  return (
    <AppShell>
      <PageHeader title="Шаблоны" />

      <div className="px-5 md:px-7">
        <form onSubmit={submitNewTemplate} className="mb-1">
          <NewTaskCard
            value={newTitle}
            onChange={setNewTitle}
            placeholder="Новый шаблон"
          />
        </form>


        {visible.length === 0 ? (
          <EmptyState
            icon={STACK}
            text={
              !local && isOfflineStandIn(props.fetchedAt)
                ? OFFLINE_EMPTY_TEXT
                : "Заведи первый многоразовый список выше 🔁 Например, список покупок — потом наполни его пунктами и запускай по кнопке «Начать»."
            }
          />
        ) : (
          <div className="-mx-2 md:-mx-4 space-y-1">
            {visible.map((task) => {
              const count = task.childrenCount ?? 0;
              return (
                <div
                  key={task.id}
                  className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50${task.leaving ? ` ${LEAVING_ROW_CLASSES}` : ""}`}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedTask(task)}
                    className="flex-1 min-w-0 text-left cursor-pointer"
                  >
                    <div className="text-sm text-gray-900 dark:text-gray-100 break-words">{task.title}</div>
                    {count > 0 && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {count} {pluralRu(count, ITEM_FORMS)}
                      </div>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => startTemplate(task.id)}
                    disabled={!online}
                    title={online ? undefined : NEEDS_NETWORK}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 cursor-pointer flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="start-template-row"
                  >
                    {PLAY}
                    <span>Начать</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {taskPendingDelete && (
        <DeleteConfirm
          title={`Удалить «${taskPendingDelete.title}»?`}
          body="Шаблон и все его пункты будут удалены безвозвратно."
          onConfirm={confirmDeleteTask}
          onCancel={() => setTaskPendingDelete(null)}
        />
      )}

      {selectedTask && (
        <DetailsPanel
          task={selectedTask}
          lists={listOptions}
          onClose={() => setSelectedTask(null)}
          onDelete={(t) => {
            setSelectedTask(null);
            setTaskPendingDelete(t);
          }}
          onTreeMutated={local ? undefined : () => flushOutbox().then(() => router.reload({ only: ["templates"] }))}
        />
      )}
    </AppShell>
  );
}
